import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Icon, Button } from '@sailor/keeta-design-pc'
import { on, off, _emit, SHOP_STATE } from '@RootCommon/utils/postMsg'
import styles from './index.less'
import { useTranslation } from '@sailor/i18n-web'
import { merchantReportMerchantStatus, getOrderDtlApi } from '../../common/api/order'
import { owlAddJsError, owlReportMetricsWeb } from '@RootCommon/utils/owl'
import { loganLog } from '@RootCommon/utils/logan'

export const COMFIRM_MODAL_EVENT = 'common_comfirm_modal'

interface Message {
  seq_no_str: string
  count_down_time: string
  order_view_id: string
  waybill_id: string
  count_down_time_string: string
  merchantSubOrderId?: string
  courierPhone?: string
  shopId: number
}

export default function CommonNotifacation () {
  const [messageList, setMessageList] = useState<Message[]>([])
  const message = useMemo(() => messageList[0], [messageList])
  const { t } = useTranslation()

  // isOpen: 0：未营业 1：营业
  const handleReportMerchantStatus = async (isOpen: number) => {
    try {
      const merchantSubOrderId = message?.merchantSubOrderId
      const reqParams: any = {
        orderViewId: message.order_view_id,
        isOpen,
        waybillId: message.waybill_id,
        shopId: message?.shopId,
      }
      if (merchantSubOrderId) {
        reqParams.merchantSubOrderId = merchantSubOrderId
      }
      await merchantReportMerchantStatus(reqParams)
      _emit(SHOP_STATE)
      // raptor上报 - 骑手上报未营业成功率
      owlReportMetricsWeb({ rider_report_not_open_success_rate: 1 })
    } catch (error) {
      // raptor上报 - 骑手上报未营业成功率
      owlReportMetricsWeb({ rider_report_not_open_success_rate: 0 })
      loganLog.error({ msg: '[骑手上报未营业]-上报店家营业状态失败', error })
    }
  }

  const getOrderInfo = async (params: {
    viewId: string;
    waybillId: string;
    shopId: number;
  }) => {
    try {
      const { orderInfo } = await getOrderDtlApi({
        viewId: params.viewId,
        shopId: params.shopId,
      })
      const deliveryInfo = orderInfo?.merchantOrderDeliveries?.find?.(item => Number(item?.waybillId) === Number(params?.waybillId))
      return {
        merchantSubOrderId: deliveryInfo?.merchantSubOrderId,
        courierPhone: deliveryInfo?.courierPhone,
      }
    } catch (error) {
      loganLog.error({ msg: '[骑手上报未营业]-获取订单详情失败', error })
    }
  }

  const handleClose = async () => {
    await handleReportMerchantStatus(0)
    setMessageList(prev => prev.filter(item => item?.order_view_id !== message?.order_view_id))
  }

  const handleConfirm = async () => {
    await handleReportMerchantStatus(1)
    setMessageList(prev => prev.slice(1))
  }

  const handleRemove = (order_view_id: string) => {
    setMessageList(prev => prev.filter(item => item?.order_view_id !== order_view_id))
  }

  const getFormatCountDownString = (count_down_time: string): string => {
    const timeLeft = Number(count_down_time) - new Date().valueOf()
    if (timeLeft <= 0) {
      return '00:00'
    }
    // 将剩余时间转换为分钟和秒
    const minutes = Math.floor(timeLeft / 60000)
      .toString()
      .padStart(2, '0')
    const seconds = Math.floor((timeLeft % 60000) / 1000)
      .toString()
      .padStart(2, '0')
    return `${minutes}:${seconds}`
  }

  useEffect(() => {
    const handleMessage = async (message: {
      seq_no_str: string
      count_down_time: string
      order_view_id: string
      waybill_id: string
      shopId: number
    }) => {
      if (!message) return
      const { merchantSubOrderId, courierPhone } = await getOrderInfo({
        viewId: message.order_view_id,
        waybillId: message.waybill_id,
        shopId: message?.shopId,
      })
      const count_down_time_string = getFormatCountDownString(message?.count_down_time)
      const seq_no_str = `#${message?.seq_no_str}`
      const waybill_id = message?.waybill_id
      setMessageList(prev => [...prev, { ...message, count_down_time_string, seq_no_str, waybill_id, merchantSubOrderId, courierPhone }])
    }
    on(COMFIRM_MODAL_EVENT, handleMessage)
    return () => {
      off(COMFIRM_MODAL_EVENT, handleMessage)
    }
  }, [])

  useEffect(() => {
    let timer = null
    timer = setInterval(() => {
      setMessageList(prev => {
        const newList = []
        prev.forEach(item => {
          const count_down_time_string = getFormatCountDownString(item?.count_down_time)
          if (count_down_time_string !== '00:00') {
            newList.push({ ...item, count_down_time_string })
          } else {
            handleRemove(item?.order_view_id)
          }
        })
        return newList
      })
    }, 1000)
    return () => {
      clearInterval(timer)
    }
  }, [])

  return (
    <Modal
      className={styles.CommonNotifacation}
      visible={messageList.length > 0}
      closeIcon={false}
      title={t('Abnormal_reporting_close_popup_title', { ns: 'pos_order_process_web', defaultValue: 'Is your restaurant opening?' })}
      children={
        <span>
          {t('Abnormal_reporting_close_popup_content', {
            ns: 'pos_order_process_web',
            orderID: <span style={{ color: '#000000', fontWeight: '500', fontFamily: 'var(--bold-font)' }}>{message?.seq_no_str}</span>,
            defaultValue: (
              <span>
                Our rider reported your restaurant might be closed for the order
                <span style={{ color: '#000000', fontWeight: '500', fontFamily: 'var(--bold-font)' }}>{message?.seq_no_str}</span>. Please confirm your restaurant status to prevent long waits for
                this order.
              </span>
            ),
          })}
        </span>
      }
      footer={
        <div className={styles.CommonNotifacation__footer}>
          <div className={styles.CommonNotifacation__footer__text}>
            <Icon name="icon-xinxi_i_yuan" size="13px" color="#5C5C5C" />
            <span>
              {t('Abnormal_reporting_close_popup_confrim_time', {
                ns: 'pos_order_process_web',
                time: <span style={{ color: '#F0390E', fontWeight: '500', fontFamily: 'var(--bold-font)' }}>{message?.count_down_time_string || ''}</span>,
                defaultValue: (
                  <span>
                    Order will be assigned to a rider in
                    <span style={{ color: '#F0390E', fontWeight: '500', fontFamily: 'var(--bold-font)' }}>7:52</span>
                  </span>
                ),
              })}
            </span>
          </div>
          <div className={styles.CommonNotifacation__footer__button}>
            <Button size="small" className={styles.CommonNotifacation__footer__button__cancel} onClick={handleClose}>
              {t('Keeta_B_closure_oPHC', { ns: 'pos_order_process_web', defaultValue: 'Closed' })}
            </Button>
            <Button size="small" className={styles.CommonNotifacation__footer__button__confirm} onClick={handleConfirm}>
              {t('Abnormal_reporting_close_popup_open', { ns: 'pos_order_process_web', defaultValue: 'Still opening' })}
            </Button>
          </div>
        </div>
      }
    />
  )
}